import { TokenManager, UserInfo } from './TokenManager';
import { ApiService } from '../utils/ApiService';
import { DeviceFingerprint } from '../utils/DeviceFingerprint';
import { SSOService, SSOResult } from './SSOService';

// A first Microsoft sign-in switches the old password off. The hint stays
// generic so it does not reveal which addresses have a Microsoft-linked account.
const INVALID_CREDENTIALS_MESSAGE =
  'Wrong email or password. If you have signed in with Microsoft before, use Sign in with Microsoft, or set a new password at bifrostledger.com/auth/forgot-password.';

export interface LoginResult {
  success: boolean;
  token?: string;
  refreshToken?: string;
  user?: UserInfo;
  error?: string;
}

export interface LicenseCheck {
  state: 'active' | 'trial' | 'expired' | 'none' | 'unknown';
}

// Shared by every AuthService in this runtime: the task pane, the status card
// and the custom functions each construct their own.
let refreshInFlight: Promise<boolean> | null = null;

export class AuthService {
  private tokenManager: TokenManager;
  private apiService: ApiService;
  private deviceFingerprint: DeviceFingerprint;
  private ssoService: SSOService;

  constructor() {
    this.tokenManager = new TokenManager();
    this.apiService = new ApiService();
    this.deviceFingerprint = new DeviceFingerprint();
    this.ssoService = new SSOService();
  }

  async trySilentSSO(): Promise<SSOResult> {
    return this.ssoService.trySilentSSO();
  }

  async trySSO(): Promise<SSOResult> {
    return this.ssoService.trySSO();
  }

  async authenticateWithSSO(): Promise<LoginResult> {
    const ssoResult = await this.ssoService.trySSO();

    if (ssoResult.success) {
      return {
        success: true,
        token: ssoResult.token,
        refreshToken: ssoResult.refreshToken,
        user: ssoResult.user
      };
    }

    return {
      success: false,
      error: ssoResult.error || 'SSO authentication failed'
    };
  }

  /**
   * Authenticate user with email and password
   */
  async login(email: string, password: string): Promise<LoginResult> {
    try {
      // Generate device fingerprint for license security
      const fingerprint = await this.deviceFingerprint.generate();
      
      const response = await this.apiService.post('/auth/login', {
        email: email.toLowerCase().trim(),
        password,
        deviceFingerprint: fingerprint
      });

      if (response.success) {
        const data = response.data as { tokens?: { accessToken?: string; refreshToken?: string }; token?: string; refreshToken?: string; user: { email: string; id: string; role: string; company?: { name: string; id: string }; companyId?: string } };
        const { tokens, user } = data;
        const accessToken = tokens?.accessToken || data.token || '';
        const refreshToken = tokens?.refreshToken || data.refreshToken || '';

        // Map backend user to UserInfo format expected by TokenManager
        const userInfo = {
          email: user.email,
          userId: user.id,
          company: user.company?.name || '',
          companyId: user.companyId || user.company?.id || '',
          permissions: [user.role]
        };

        // Store token for cross-document access
        await this.tokenManager.storeToken(accessToken, userInfo, refreshToken);

        return {
          success: true,
          token: accessToken,
          refreshToken,
          user: userInfo
        };
      } else {
        return {
          success: false,
          error: response.error || 'Login failed'
        };
      }

    } catch (error) {
      console.error('Login error:', error);

      // Handle specific error types
      if (error instanceof Error) {
        const errorCode = (error as Error & { code?: string }).code;

        // Handle specific error codes from backend
        if (errorCode === 'DEVICE_LOCKED') {
          return {
            success: false,
            error: error.message
          };
        }

        if (errorCode === 'AUTH_RATE_LIMIT_EXCEEDED') {
          return {
            success: false,
            error: 'Too many login attempts. Please wait a few minutes before trying again.'
          };
        }

        if (errorCode === 'ACCOUNT_DISABLED') {
          return {
            success: false,
            error: 'This account has been deactivated. Please contact your administrator.'
          };
        }

        if (errorCode === 'INVALID_CREDENTIALS') {
          return {
            success: false,
            error: INVALID_CREDENTIALS_MESSAGE
          };
        }

        if (error.message.includes('network') || error.message.includes('fetch')) {
          return {
            success: false,
            error: 'Network connection failed. Please check your internet connection.'
          };
        }

        if (error.message.includes('unauthorized') || error.message.includes('401')) {
          return {
            success: false,
            error: INVALID_CREDENTIALS_MESSAGE
          };
        }

        if (error.message.includes('license')) {
          return {
            success: false,
            error: 'Your license has expired or is invalid. Please contact your administrator.'
          };
        }

        // If we have a specific error message from backend, use it
        if (errorCode) {
          return {
            success: false,
            error: error.message
          };
        }
      }

      return {
        success: false,
        error: 'An unexpected error occurred. Please try again.'
      };
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = await this.tokenManager.getRefreshToken();
      
      if (!refreshToken) {
        return false;
      }
      
      const fingerprint = await this.deviceFingerprint.generate();
      
      // The body is validated strictly: any key besides refreshToken is a 400,
      // which this method reads as transient, so the session could never renew.
      const response = await this.apiService.post('/auth/refresh', { refreshToken }, {
        headers: { 'x-device-fingerprint': fingerprint }
      });

      if (response.success) {
        // /auth/refresh nests its payload under `tokens`; login/SSO return it
        // flat. Tolerate both — reading only the flat shape meant every refresh
        // stored an undefined token, threw inside storeToken and silently
        // returned false, so signed-in users were bounced to the login screen
        // the moment their access token aged out (and re-login drops you back
        // on your home company, losing a switched-in client/demo company).
        const data = response.data as {
          tokens?: { accessToken?: string; refreshToken?: string };
          token?: string;
          refreshToken?: string;
          user?: UserInfo;
        };
        const accessToken = data.tokens?.accessToken || data.token || '';
        const newRefreshToken = data.tokens?.refreshToken || data.refreshToken || refreshToken;

        if (!accessToken) {
          // 2xx we can't parse — treat as transient and keep the session.
          console.warn('Token refresh returned no access token; keeping existing session');
          return false;
        }

        const user = data.user ?? await this.userInfoFromAccessToken(accessToken);
        await this.tokenManager.storeToken(accessToken, user, newRefreshToken);

        return true;
      }

      // Only clear tokens when the server definitively rejects the refresh token
      if (response.status === 401 || response.status === 403) {
        // Every open workbook shares this token store but runs its own runtime,
        // so another workbook may have renewed with the same refresh token
        // while this request was in flight. Clearing then would sign out every
        // workbook, the one that just renewed included.
        const current = await this.tokenManager.getRefreshToken();
        if (current && current !== refreshToken) {
          return (await this.tokenManager.getToken()) !== null;
        }
        await this.tokenManager.clearToken();
      } else {
        console.warn('Token refresh failed with transient error:', response.error);
      }
      return false;

    } catch (error) {
      // Non-API errors (storage failure, fingerprint) — don't destroy a valid session
      console.warn('Token refresh error:', error);
      return false;
    }
  }

  /**
   * The company's subscription status, as /license/validate derives it from
   * the subscription (expiry-aware, via canExecuteFormulas/evaluateSubscription).
   * NOT the same as "am I authenticated" — a signed-in user with an expired or
   * absent subscription must not read as active.
   *
   * 'unknown' means the check itself failed (offline, server error). Reporting
   * that as expired sends the user to fix a plan that is fine.
   */
  async checkLicense(): Promise<LicenseCheck> {
    try {
      const token = await this.tokenManager.getToken();
      if (!token) {
        return { state: 'unknown' };
      }

      const fingerprint = await this.deviceFingerprint.generate();
      const response = await this.apiService.get<{ valid?: boolean; reason?: string }>('/license/validate', {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-device-fingerprint': fingerprint
        }
      });

      const data = response.success ? response.data : undefined;
      if (!data || typeof data.valid !== 'boolean') {
        return { state: 'unknown' };
      }
      if (data.valid) {
        return { state: data.reason === 'ACTIVE_TRIAL' ? 'trial' : 'active' };
      }
      // NO_LICENSE covers both "never subscribed" and a cancelled or suspended
      // subscription; the response does not say which.
      return { state: data.reason === 'EXPIRED' ? 'expired' : 'none' };
    } catch (error) {
      console.error('License validation error:', error);
      return { state: 'unknown' };
    }
  }

  /**
   * Sign out user (clear local tokens)
   */
  async logout(): Promise<void> {
    try {
      const token = await this.tokenManager.getToken();
      
      if (token) {
        // Notify backend of logout (optional, for audit logging)
        try {
          await this.apiService.post('/auth/logout', {}, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        } catch (error) {
          // Ignore backend logout errors
          console.warn('Backend logout failed:', error);
        }
      }
      
      // Clear local storage
      await this.tokenManager.clearToken();
      
    } catch (error) {
      console.error('Logout error:', error);
      // Clear tokens even if there's an error
      await this.tokenManager.clearToken();
    }
  }

  /**
   * Check if user is currently authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      // getToken() returns a token only when it is valid (non-expired) and no
      // longer clears expired tokens, so the refresh token survives below.
      const token = await this.tokenManager.getToken();
      if (token) {
        return true;
      }

      // No valid access token. Attempt a silent refresh if a refresh token is
      // still available. This keeps already-signed-in users authenticated —
      // e.g. custom functions recalculating after the 1h access token aged out
      // — instead of surfacing #SIGN_IN despite a renewable session.
      const refreshToken = await this.tokenManager.getRefreshToken();
      if (!refreshToken) {
        return false;
      }
      return await this.renewSession();

    } catch (error) {
      console.error('Authentication check error:', error);
      return false;
    }
  }

  /**
   * /auth/refresh returns tokens only — no user object. Rebuild UserInfo from
   * the new access token's claims, the same mapping switchCompany uses in the
   * task pane. The JWT carries companyId but not the company NAME, so the
   * stored name is kept while the active company is unchanged.
   */
  private async userInfoFromAccessToken(accessToken: string): Promise<UserInfo> {
    const claims = this.tokenManager.decodeJWT(accessToken) as {
      email?: string;
      userId?: string;
      companyId?: string;
      role?: string;
    };
    const stored = await this.tokenManager.getUserInfo();
    // The backend reverts to the home company when access to a switched-in
    // company was revoked. The stored name would then name the wrong company,
    // so drop it and let the company switcher resolve the name from
    // /external-access/my-companies on the next render.
    const sameCompany = !!stored?.companyId && stored.companyId === claims.companyId;

    return {
      email: claims.email ?? stored?.email ?? '',
      userId: claims.userId ?? stored?.userId ?? '',
      company: sameCompany ? (stored?.company ?? '') : '',
      companyId: claims.companyId ?? stored?.companyId ?? '',
      permissions: claims.role ? [claims.role] : (stored?.permissions ?? []),
    };
  }

  /**
   * Refresh the session, coalescing concurrent callers onto a single in-flight
   * request. Multiple custom-function batches can call isAuthenticated()
   * simultaneously; without coalescing each would POST /auth/refresh and race
   * on refresh-token rotation, causing all but the first to 401 and wipe the
   * session.
   *
   * Public because the custom-function paths must renew before deciding a 401
   * is terminal. `refreshToken()` clears the session only when the server
   * definitively rejects the refresh token, so callers should reach for this
   * rather than clearing tokens on any error that mentions auth.
   */
  renewSession(): Promise<boolean> {
    if (!refreshInFlight) {
      refreshInFlight = this.refreshToken().finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  }

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<UserInfo | null> {
    try {
      const isAuth = await this.isAuthenticated();
      
      if (!isAuth) {
        return null;
      }
      
      return await this.tokenManager.getUserInfo();

    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }
}